import React, { useState } from 'react';
import axios from 'axios';
import Navigation from './Navigation';
import { API_ENDPOINTS } from '../config/api';
import './ContributePage.css';

const ContributePage = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    museum: '',
    museumNumber: '',
    originalLocation: '',
    dimensions: '',
    material: '',
    period: '',
    condition: '',
    latitude: '',
    longitude: '',
    image: null,
    imageCaption: '',
    photographer: '',
    copyright: '',
    tags: '',
    referencesCitation: '',
    referenceURL: '',
    name: '',
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create FormData object to handle file upload
      const submitData = new FormData();
      
      // Add all text fields
      Object.keys(formData).forEach(key => {
        if (key !== 'image' && formData[key]) {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add image file if present
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      // Submit to API
      const response = await axios.post(API_ENDPOINTS.submit, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Handle success
      setSubmitStatus({
        type: 'success',
        message: response.data.message || 'Your submission has been received!'
      });
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        museum: '',
        museumNumber: '',
        originalLocation: '',
        dimensions: '',
        material: '',
        period: '',
        condition: '',
        latitude: '',
        longitude: '',
        image: null,
        imageCaption: '',
        photographer: '',
        copyright: '',
        tags: '',
        referencesCitation: '',
        referenceURL: '',
        name: '',
        email: ''
      });
      
      // Reset file input
      const fileInput = document.getElementById('image');
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.error || 'An error occurred while submitting. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contribute-page">
      <Navigation />
      <div className="content">
        <h1>Contribute to the Archive</h1>
        <p style={{color: 'red', fontSize: '12px'}}>Build v0.1.1 - Form submission enabled</p>
        
        <p className="intro-text">
          Help us document and preserve Neo-Assyrian bas-reliefs by contributing to our archive. Please provide as much detail as 
          possible to ensure accuracy and completeness.
        </p>

        {submitStatus && (
          <div className={`status-message ${submitStatus.type}`}>
            {submitStatus.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="contribution-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
            <small>A descriptive title for the relief.</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows="4"
            />
            <small>Provide a detailed description of the relief, including its subject matter and historical context.</small>
          </div>

          <div className="form-group">
            <label htmlFor="museum">Museum</label>
            <input
              type="text"
              id="museum"
              name="museum"
              value={formData.museum}
              onChange={handleInputChange}
            />
            <small>Current museum or institution housing the relief.</small>
          </div>

          <div className="form-group">
            <label htmlFor="museumNumber">Museum Number</label>
            <input
              type="text"
              id="museumNumber"
              name="museumNumber"
              value={formData.museumNumber}
              onChange={handleInputChange}
            />
            <small>Museum's catalog or accession number.</small>
          </div>

          <div className="form-group">
            <label htmlFor="originalLocation">Original Location</label>
            <input
              type="text"
              id="originalLocation"
              name="originalLocation"
              value={formData.originalLocation}
              onChange={handleInputChange}
            />
            <small>Original location within the palace, if known.</small>
          </div>

          <div className="form-group">
            <label htmlFor="dimensions">Dimensions</label>
            <input
              type="text"
              id="dimensions"
              name="dimensions"
              value={formData.dimensions}
              onChange={handleInputChange}
            />
            <small>Physical dimensions (height × width × depth).</small>
          </div>

          <div className="form-group">
            <label htmlFor="material">Material</label>
            <input
              type="text"
              id="material"
              name="material"
              value={formData.material}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="period">Period</label>
            <input
              type="text"
              id="period"
              name="period"
              value={formData.period}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="condition">Condition</label>
            <input
              type="text"
              id="condition"
              name="condition"
              value={formData.condition}
              onChange={handleInputChange}
            />
            <small>Current state of preservation.</small>
          </div>

          <div className="form-section">
            <h2>Geographic Location</h2>
            <div className="map-container">
              {/* TODO: Add interactive map component */}
              <div className="placeholder-map">Map will be displayed here</div>
            </div>
            <div className="form-group">
              <label htmlFor="latitude">Latitude</label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="longitude">Longitude</label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Image Information</h2>
            <div className="form-group">
              <label htmlFor="image">Image *</label>
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleImageChange}
                accept="image/*"
              />
              <small>Upload a high-quality image of the relief.</small>
            </div>

            <div className="form-group">
              <label htmlFor="imageCaption">Image Caption</label>
              <input
                type="text"
                id="imageCaption"
                name="imageCaption"
                value={formData.imageCaption}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="photographer">Photographer</label>
              <input
                type="text"
                id="photographer"
                name="photographer"
                value={formData.photographer}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="copyright">Copyright Information</label>
              <input
                type="text"
                id="copyright"
                name="copyright"
                value={formData.copyright}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
            />
            <small>Enter tags separated by commas (e.g., "hunting scene, lion, king").</small>
          </div>

          <div className="form-group">
            <label htmlFor="referencesCitation">Reference Citation</label>
            <textarea
              id="referencesCitation"
              name="referencesCitation"
              value={formData.referencesCitation}
              onChange={handleInputChange}
              rows="3"
            />
            <small>Academic citation for the relief.</small>
          </div>

          <div className="form-group">
            <label htmlFor="referenceURL">Reference URL</label>
            <input
              type="url"
              id="referenceURL"
              name="referenceURL"
              value={formData.referenceURL}
              onChange={handleInputChange}
            />
            <small>URL to additional information or documentation.</small>
          </div>

          <div className="form-section">
            <h2>Contact Information</h2>
            <div className="form-group">
              <label htmlFor="name">Your Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Your Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <small>We'll notify you when your submission is approved.</small>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Contribution'}
          </button>
        </form>
      </div>

      <footer>
        <p>2024 Bas Relief Atlas - A Digital Archive of Neo-Assyrian Bas-Reliefs</p>
      </footer>
    </div>
  );
};

export default ContributePage; 